import React, { FC, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { WorkoutData } from '../../../models/training.models';
import useService from '../../../hooks/service.hook';
import EquipmentService from '../../../services/equipments.service';
import EquipmentSelectDialog from '../components/equipment-select-dialog';
import LinkedEquipmentCard, { LinkedEquipmentDetails } from '../components/linked-equipment-card';
import { canEditTrainingEquipment } from '../components/use-link-equipment.hook';
import { palette } from '../../../theme';

export interface EquipmentTabProps {
  workoutData: WorkoutData | null | undefined;
  trainingId: number;
  trainingType: 'athlete' | 'coach' | 'guardian';
  onEquipmentLinked?: () => void;
}

const EquipmentTab: FC<EquipmentTabProps> = ({ workoutData, trainingId, trainingType, onEquipmentLinked }) => {
  const equipmentService = useService(EquipmentService);
  const linkedEquipment = workoutData?.equipment;
  const linkedEquipmentId = linkedEquipment?.id;
  const canEditEquipment = canEditTrainingEquipment(trainingId, trainingType, workoutData);

  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const [equipmentDetails, setEquipmentDetails] = useState<LinkedEquipmentDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(false);

  useEffect(() => {
    if (linkedEquipmentId == null) {
      setEquipmentDetails(null);
      setDetailsLoading(false);
      setDetailsError(false);
      return;
    }

    let cancelled = false;
    setDetailsLoading(true);
    setDetailsError(false);

    equipmentService
      .getEquipment(linkedEquipmentId)
      .then((res) => {
        if (cancelled) return;
        setEquipmentDetails(res.data?.data ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setEquipmentDetails(null);
        setDetailsError(true);
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [equipmentService, linkedEquipmentId]);

  const handleOpenSelectDialog = () => setSelectDialogOpen(true);
  const handleCloseSelectDialog = () => setSelectDialogOpen(false);

  if (linkedEquipmentId != null && detailsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 240,
          p: 3,
        }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (linkedEquipmentId != null && (equipmentDetails || detailsError)) {
    const cardEquipment: LinkedEquipmentDetails =
      equipmentDetails ??
      ({
        id: linkedEquipmentId,
        name: linkedEquipment?.name ?? 'Equipment',
        brand: linkedEquipment?.brand,
        year: linkedEquipment?.year,
      } as LinkedEquipmentDetails);

    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560, mx: 'auto', width: '100%' }}>
        <LinkedEquipmentCard equipment={cardEquipment} />

        {canEditEquipment && (
          <Button
            variant="outlined"
            onClick={handleOpenSelectDialog}
            endIcon={<ArrowForwardIcon />}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: palette.divider,
              color: palette.text.primary,
            }}
          >
            Change Equipment
          </Button>
        )}

        <EquipmentSelectDialog
          open={selectDialogOpen}
          onClose={handleCloseSelectDialog}
          workoutData={workoutData}
          trainingId={trainingId}
          trainingType={trainingType}
          onEquipmentLinked={onEquipmentLinked}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 280,
        gap: 1.5,
        maxWidth: 480,
        mx: 'auto',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          bgcolor: palette.grey[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
        }}
      >
        <Inventory2OutlinedIcon sx={{ fontSize: 28, color: palette.grey[400] }} />
      </Box>

      <Typography variant="h6" fontWeight={700} color={palette.text.primary}>
        No Equipment Added
      </Typography>

      <Typography variant="body2" color={palette.text.secondary} sx={{ maxWidth: 320 }}>
        Attach the gear used for this session — skis, bikes, boots, and more.
      </Typography>

      {canEditEquipment && (
        <Button
          variant="contained"
          onClick={handleOpenSelectDialog}
          sx={{
            mt: 1,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
          }}
        >
          + Select Equipment
        </Button>
      )}

      <EquipmentSelectDialog
        open={selectDialogOpen}
        onClose={handleCloseSelectDialog}
        workoutData={workoutData}
        trainingId={trainingId}
        trainingType={trainingType}
        onEquipmentLinked={onEquipmentLinked}
      />
    </Box>
  );
};

export default EquipmentTab;
